import type { Attachment, DocumentReference } from '@medplum/fhirtypes'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { getBinaryId, saveBlobWithName } from '@seen/core/utils'
import { Button } from '@seen/ui'
import { IconDownload, IconX } from '@tabler/icons-react'
import { type ReactNode, useCallback } from 'react'
import { DropdownMenuDeleteItem } from '#src/components/core/dropdown-menu/DropdownMenuDeleteItem.tsx'
import type { DocumentPickerDocument } from '#src/components/document-picker/DocumentPickerView.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import type { Filter, FilterConfigMap } from '#src/components/filter/Filter.tsx'
import { FilterComposer } from '#src/components/filter/FilterComposer.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useReference } from '#src/hooks/useReference.ts'
import { useShowMedplumLink } from '#src/hooks/useShowMedplumLink.ts'

interface DocumentPickerHeaderProps {
  filterOptions: FilterConfigMap<DocumentPickerDocument>
  onFiltersChange: (filters: Filter[]) => void
  documentId?: string | null
  onClose?: () => void
}

export function DocumentPickerHeader({
  filterOptions,
  onFiltersChange,
  documentId,
  onClose,
}: DocumentPickerHeaderProps): ReactNode {
  return (
    <div className="flex min-h-12 shrink-0 items-center gap-2 border-b px-2">
      <div className="min-w-0 flex-1">
        <FilterComposer<DocumentPickerDocument>
          id="document-picker-filters"
          filterOptions={filterOptions}
          onChange={onFiltersChange}
        />
      </div>
      <div className="-mr-2 flex shrink-0 items-center">
        {documentId && <DocumentPickerHeaderActions documentId={documentId} />}
        {onClose && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            className="text-muted-foreground"
          >
            <IconX className="size-4" />
          </Button>
        )}
      </div>
    </div>
  )
}

function DocumentPickerHeaderActions({
  documentId,
}: {
  documentId: string
}): ReactNode {
  const isReadOnly = useIsReadOnly()
  const showMedplumLink = useShowMedplumLink()
  const medplum = useMedplum()
  const documentReference = useReference<DocumentReference>(
    `DocumentReference/${documentId}`
  )
  const { data: docRef } = useRead(ExtendedDocumentReference, documentReference)
  const patch = usePatchMutation<ExtendedDocumentReference>()

  const downloadAll = useCallback(async () => {
    const attachments: Attachment[] = docRef?.attachments ?? []
    if (attachments.length === 0) {
      return
    }

    const downloadOne = async (attachment: Attachment, index: number) => {
      try {
        if (!attachment.url) {
          return
        }
        /* stagger downloads to avoid browser popup blockers */
        if (index > 0) {
          await new Promise((resolve) => setTimeout(resolve, index * 100))
        }
        const blob = await medplum.downloadAttachmentAsBinary(attachment.url)
        saveBlobWithName(blob, getBinaryId(attachment.url) ?? attachment.title)
      } catch (error) {
        console.error('Failed to download attachment', attachment, error)
      }
    }

    await Promise.allSettled(
      attachments.map((attachment, i) => downloadOne(attachment, i))
    )
  }, [docRef?.attachments, medplum])

  const hasAttachments =
    docRef?.attachments != null && docRef.attachments.length > 0

  return (
    <>
      {hasAttachments && (
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground"
          onClick={downloadAll}
        >
          <IconDownload className="size-4" />
        </Button>
      )}
      {docRef && (!isReadOnly || showMedplumLink) && (
        <MoreDropdownMenu size="icon-sm">
          {!isReadOnly && (
            <DropdownMenuDeleteItem
              deleteConfirmationProps={{
                title: 'Delete document',
                description: 'Are you sure you want to delete this document?',
                onConfirm: () => {
                  patch.mutate({
                    instance: docRef,
                    mutateFn(docRef) {
                      docRef.markDeleted()
                    },
                  })
                },
              }}
            />
          )}
          <DropdownMenuMedplumLinkItem to={docRef} />
        </MoreDropdownMenu>
      )}
    </>
  )
}
